import React from "react";
import { usePollingInterval } from "@/lib/utils/usePollingInterval/usePollingInterval";
import { LoadingScreenComponent } from "@/components/LoadingScreen/LoadingScreenComponent";
import { logger } from "@/lib/utils/logger/logger";
import { VerificationStepsEnum } from "@/lib/types/runtimeTypes";
import { IDCheckLoopResponse, VerificationService } from "@/lib/types/types";
import { base64ToFile, uploadFiles } from "../utils/utils";
import { useRegulaSdk } from "./hooks/useRegulaSdk";
import { IdCheckReviewScreen } from "./IdCheckReviewScreen";
import { REGULA_SCENARIO } from "./constants";

export interface IdCheckLoopRegulaProps {
  verificationService: VerificationService;
}

const MOBILE_HANDOFF_POLLING_DURATION = 6000;

export const IdCheckLoopRegula = ({ verificationService }: IdCheckLoopRegulaProps) => {
  const verificationResponse = verificationService.verificationResponse as IDCheckLoopResponse;
  const { uploadUrl, verificationId, locale } = verificationResponse;

  const [submitInitiated, setSubmitInitiated] = React.useState<boolean>(false);
  const [currentUploadAttempt, setCurrentUploadAttempt] = React.useState(1);
  const [startPolling, setStartPolling] = React.useState(false);

  usePollingInterval(
    () => {
      if (!startPolling) return;
      verificationService.fetchExistingVerificationRequest(
        verificationService.programId,
        verificationId,
        verificationResponse,
        verificationService.previousViewModel,
        false,
      );
    },
    startPolling ? MOBILE_HANDOFF_POLLING_DURATION : null,
  );

  const { frontImage, backImage, hasImageError, handleRetry, readerRef } = useRegulaSdk({
    verificationId,
    locale,
    currentUploadAttempt,
    setCurrentUploadAttempt,
    setStartPolling,
  });

  const handleSubmit = () => {
    setSubmitInitiated(true);
    uploadFiles(
      uploadUrl,
      base64ToFile(frontImage, "front.jpg"),
      backImage ? base64ToFile(backImage, "back.jpg") : undefined,
    )
      .then((response: Response) => {
        if (response.ok) {
          verificationService.submitStep(
            VerificationStepsEnum.idCheckLoop,
            verificationService.viewModel,
            verificationService.verificationResponse,
          );
        } else {
          logger.error(
            `IdCheckLoop step failed to upload files to: ${uploadUrl}. ${response.status}`,
          );
          setSubmitInitiated(false);
        }
      })
      .catch((e) => {
        logger.error(`IdCheckLoop step failed to upload files to: ${uploadUrl}. ${e}`);
        setSubmitInitiated(false);
      });
  };

  if (submitInitiated) {
    return <LoadingScreenComponent step="idCheckLoop" />;
  }

  return (
    <div>
      {!frontImage ? (
        // Regula web sdk component
        <document-reader
          ref={readerRef}
          internal-scenario={REGULA_SCENARIO}
          key={`reader-${currentUploadAttempt}`} // forces remount to reset Regula SDK on retry
        />
      ) : (
        <IdCheckReviewScreen
          frontImage={frontImage}
          backImage={backImage}
          hasImageError={hasImageError}
          currentUploadAttempt={currentUploadAttempt}
          onRetry={handleRetry}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};

export const IdCheckLoopRegulaComponent = IdCheckLoopRegula;
